import React, { useState, useEffect } from 'react';
import { deleteProduct, Product, addProduct, uploadImage } from '../../firebase/productService';
import AdminProductForm from './AdminProductForm';
import AdminAuth from './AdminAuth';
import './AdminPanel.css';
import { useProducts } from '../../context/ProductContext';

const AdminPanel: React.FC = () => {
  const { products, loading, error, refreshProducts } = useProducts();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [updatingUrls, setUpdatingUrls] = useState(false);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        await refreshProducts();
      } catch (err) {
        console.error('Error deleting product:', err);
      }
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleFormSubmit = async () => {
    await refreshProducts();
    handleFormClose();
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };



  // Filter products based on search and type
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || product.type === selectedType;
    return matchesSearch && matchesType;
  });

  // Get unique types for filter
  const types = ['all', ...Array.from(new Set(products.map(p => p.type)))];

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  if (!isAuthenticated) {
    return <AdminAuth onLogin={handleLogin} />;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="header-content">
          <h1>Product Management</h1>
          <div className="user-info">
            <span>Welcome, Admin</span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </div>

             

       



      <div className="admin-controls">
        <div className="admin-controls-left">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="category-filter"
          >
            {types.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Categories' : type}
              </option>
            ))}
          </select>
          <button onClick={handleAdd} className="add-product-btn">
            + Add Product
          </button>
        </div>
        <div className="admin-controls-right">
          <button 
            className="pagination-btn" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            {currentPage} of {totalPages}
          </span>
          <button 
            className="pagination-btn" 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading products...</div>}
      
      {error && <div className="error">Error: {error}</div>}



      {!loading && !error && (
        <>
                     <div className="products-table">
             <div className="table-header">
               <div className="table-cell checkbox-cell">
                 <input type="checkbox" />
               </div>
               <div className="table-cell">PRODUCT NAME</div>
               <div className="table-cell">PRICE</div>
               <div className="table-cell">CATEGORY</div>
               <div className="table-cell">PUBLISHED ON</div>
               <div className="table-cell actions-cell"></div>
             </div>
             
             {currentProducts.map((product) => (
               <div key={product.id} className="table-row">
                 <div className="table-cell checkbox-cell">
                   <input type="checkbox" />
                 </div>
                 <div className="table-cell product-cell">
                   <div className="product-info">
                     <img 
                       src={product.img} 
                       alt={product.name} 
                       className="product-thumbnail"
                     />
                     <span className="product-name">{product.name}</span>
                   </div>
                 </div>
                 <div className="table-cell">${product.price}</div>
                 <div className="table-cell">{product.type || 'Uncategorized'}</div>
                 <div className="table-cell">{product.release ? new Date(product.release).toLocaleDateString() : 'N/A'}</div>
                 <div className="table-cell actions-cell">
                   <div className="actions-dropdown">
                     <button className="actions-btn">⋯</button>
                     <div className="actions-menu">
                       <button onClick={() => handleEdit(product)}>Edit</button>
                       <button onClick={() => handleDelete(product.id!)}>Delete</button>
                     </div>
                   </div>
                 </div>
               </div>
             ))}
           </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showForm && (
        <AdminProductForm
          product={editingProduct}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default AdminPanel; 